import { useState, useEffect, useCallback } from 'react';
import { userSettingsApi } from '../services/api';
import { setMoneySettings } from '../utils/money';

// Module-level cache so every component shares one request
let cached = null;
let fetchPromise = null;
const listeners = new Set();

function fetchSettings() {
  if (!fetchPromise) {
    fetchPromise = userSettingsApi.get()
      .then(res => {
        cached = res.data;
        setMoneySettings(cached);
        listeners.forEach(fn => fn(cached));
        return cached;
      })
      .catch(err => {
        console.error('Failed to load user settings:', err.message);
        fetchPromise = null; // Allow retry on next mount
        return null;
      });
  }
  return fetchPromise;
}

/**
 * The user's settings: { autoProcessForwarded, defaultAccountType, currency, numberFormat, canChooseModels, models? }.
 * Currency and number format also feed utils/money, which every amount on screen goes through.
 * Only whitelisted users can choose (or see) AI models.
 * Returns { settings, canChooseModels, loading, update }
 */
export function useUserSettings() {
  const [settings, setSettings] = useState(cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    const listener = (value) => setSettings(value);
    listeners.add(listener);
    if (cached) {
      setLoading(false);
    } else {
      fetchSettings().then(value => {
        setSettings(value);
        setLoading(false);
      });
    }
    return () => listeners.delete(listener);
  }, []);

  const update = useCallback(async (changes) => {
    const res = await userSettingsApi.update(changes);
    cached = res.data.settings;
    setMoneySettings(cached);
    fetchPromise = Promise.resolve(cached);
    listeners.forEach(fn => fn(cached));
    return cached;
  }, []);

  return { settings, canChooseModels: !!settings?.canChooseModels, loading, update };
}
